import { AUDIO_SUFFIXES, IMAGE_SUFFIXES, MESH_SOURCE_SPEC, acceptsPath, assetDisplayName, toAssetValue } from './asset-types.js';
import type { EditorCommand, EntityDesc, SceneDoc, Vec3 } from './scene-doc.js';

/**
 * What dropping a project file from the Assets panel does (Unity's drag and
 * drop into the Scene view / Hierarchy):
 *
 * - models and prefabs spawn an entity (at the surface under the cursor in
 *   the scene view, as a child when dropped on a Hierarchy row);
 * - materials, textures, scripts, audio clips, animator controllers, UI
 *   documents and physics materials dropped on an entity are assigned to it
 *   (textures/clips/scripts/UI documents dropped on empty space create a
 *   sprite / audio source / scripted / UI entity);
 * - scenes open.
 *
 * Pure: returns editor commands for the scene store to apply.
 */

export type DropTarget =
  /** Scene view: `point` from the engine's drop raycast (null without an engine), `hit` = entity under the cursor. */
  | { kind: 'scene'; point: Vec3 | null; hit: string | null }
  /** A Hierarchy row. */
  | { kind: 'entity'; id: string }
  /** Empty Hierarchy space. */
  | { kind: 'root' };

export interface DropPlan {
  commands: EditorCommand[];
  /** Open this file instead (scenes). */
  open?: string;
  /** Shown when the drop does nothing (e.g. a material on empty space). */
  message?: string;
}

export interface DropContext {
  /** Contents of `*.material.json` files the target uses, keyed by path (so a dropped texture keeps the material's look). */
  materials?: Readonly<Record<string, Record<string, unknown>>>;
}

export type DropKind = 'model' | 'prefab' | 'material' | 'texture' | 'audio' | 'script' | 'controller' | 'ui' | 'physicsMaterial' | 'scene';

/** How a file behaves when dropped, or null when it cannot be dropped into a scene. */
export function dropKind(path: string): DropKind | null {
  const p = path.toLowerCase();
  if (acceptsPath(MESH_SOURCE_SPEC, p)) return 'model';
  if (p.endsWith('.prefab.json')) return 'prefab';
  if (p.endsWith('.material.json')) return 'material';
  if (p.endsWith('.physicsmat.json')) return 'physicsMaterial';
  if (p.endsWith('.controller.json')) return 'controller';
  if (p.endsWith('.ui.json')) return 'ui';
  if (p.endsWith('.scene.json')) return 'scene';
  if (p.endsWith('.rhai')) return 'script';
  if (IMAGE_SUFFIXES.some((s) => p.endsWith(s))) return 'texture';
  if (AUDIO_SUFFIXES.some((s) => p.endsWith(s))) return 'audio';
  return null;
}

/** Short verb for the drop hint ("Place", "Assign material"...). */
export function dropLabel(path: string, onEntity: boolean): string | null {
  const kind = dropKind(path);
  if (!kind) return null;
  const name = assetDisplayName(path);
  switch (kind) {
    case 'model':
    case 'prefab':
      return `Place ${name}`;
    case 'scene':
      return `Open ${name}`;
    case 'material':
      return onEntity ? `Assign material ${name}` : null;
    case 'physicsMaterial':
      return onEntity ? `Assign physics material ${name}` : null;
    case 'controller':
      return onEntity ? `Animate with ${name}` : null;
    case 'texture':
      return onEntity ? `Assign texture ${name}` : `Place sprite ${name}`;
    case 'audio':
      return onEntity ? `Add audio source ${name}` : `Place audio source ${name}`;
    case 'script':
      return onEntity ? `Add script ${name}` : `Place ${name}`;
    case 'ui':
      return onEntity ? `Show UI ${name}` : `Place UI ${name}`;
  }
}

const obj = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

function round(v: Vec3): Vec3 {
  return v.map((n) => Math.round(n * 1000) / 1000) as Vec3;
}

export function planAssetDrop(doc: SceneDoc, path: string, target: DropTarget, context: DropContext = {}): DropPlan {
  const kind = dropKind(path);
  if (!kind) return { commands: [], message: `${assetDisplayName(path)} cannot be dropped into a scene.` };
  if (kind === 'scene') return { commands: [], open: path };

  const entityId = target.kind === 'entity' ? target.id : target.kind === 'scene' ? target.hit : null;
  const entity = entityId ? (doc.entities.find((e) => e.id === entityId) ?? null) : null;
  const name = assetDisplayName(path);

  /** A new entity: in the scene view at the drop point, from the Hierarchy as a child of the row. */
  const spawn = (components: Record<string, unknown>, label = name): DropPlan => {
    const parent = target.kind === 'entity' ? target.id : null;
    const position: Vec3 = target.kind === 'scene' && target.point ? round(target.point) : [0, 0, 0];
    return {
      commands: [{ cmd: 'addEntity', desc: { id: '', name: label, parent, transform: { position }, components }, parent }],
    };
  };
  const set = (e: EntityDesc, key: string, value: unknown): EditorCommand => ({ cmd: 'setComponent', id: e.id, key, value });

  switch (kind) {
    case 'model':
      return spawn({ mesh: { source: toAssetValue(MESH_SOURCE_SPEC, path) } });
    case 'prefab':
      return spawn({ prefab: { path } });
    case 'material': {
      if (!entity) return { commands: [], message: 'Drop a material onto an object.' };
      const c = entity.components ?? {};
      if (c.mesh !== undefined) {
        // An inline material wins over mesh.material: drop it so the asset shows.
        const commands: EditorCommand[] = [set(entity, 'mesh', { ...obj(c.mesh), material: path })];
        if (c.material !== undefined) commands.push({ cmd: 'removeComponent', id: entity.id, key: 'material' });
        return { commands };
      }
      if (c.particleSystem !== undefined) {
        const ps = obj(c.particleSystem);
        return { commands: [set(entity, 'particleSystem', { ...ps, renderer: { ...obj(ps.renderer), material: path } })] };
      }
      if (c.terrain !== undefined) return { commands: [set(entity, 'terrain', { ...obj(c.terrain), material: path })] };
      return { commands: [], message: `${entity.name ?? entity.id} has no mesh to put a material on.` };
    }
    case 'texture': {
      const pixel = path.toLowerCase().endsWith('.pixel.json');
      if (!entity) {
        return spawn({ sprite: pixel ? { sheet: path, animation: '' } : { image: path } });
      }
      const c = entity.components ?? {};
      if (c.sprite !== undefined) {
        const sprite = { ...obj(c.sprite) };
        if (pixel) sprite.sheet = path;
        else sprite.image = path;
        return { commands: [set(entity, 'sprite', sprite)] };
      }
      if (c.particleSystem !== undefined && c.mesh === undefined) {
        const ps = obj(c.particleSystem);
        return { commands: [set(entity, 'particleSystem', { ...ps, renderer: { ...obj(ps.renderer), texture: path } })] };
      }
      // The entity's inline material, else a copy of its material asset (keeps its look), plus the texture.
      const mesh = obj(c.mesh);
      const asset = typeof mesh.material === 'string' ? context.materials?.[mesh.material] : undefined;
      let base: Record<string, unknown> = {};
      if (c.material !== undefined) base = obj(c.material);
      else if (asset) {
        const { format: _format, name: _name, ...rest } = asset;
        base = rest;
      }
      return { commands: [set(entity, 'material', { ...base, texture: path })] };
    }
    case 'audio': {
      if (!entity) return spawn({ audioSource: { clip: path, volume: 1, loop: false, playOnAwake: true, spatialBlend: 1 } });
      const current = obj(entity.components?.audioSource);
      const value = Object.keys(current).length ? { ...current, clip: path } : { clip: path, volume: 1, loop: false, playOnAwake: true, spatialBlend: 0 };
      return { commands: [set(entity, 'audioSource', value)] };
    }
    case 'script': {
      if (!entity) return spawn({ script: { path, props: {} } });
      const current = obj(entity.components?.script);
      return { commands: [set(entity, 'script', { ...current, path, props: obj(current.props) })] };
    }
    case 'controller': {
      if (!entity) return { commands: [], message: 'Drop an animator controller onto a character.' };
      return { commands: [set(entity, 'animator', { ...obj(entity.components?.animator), controller: path })] };
    }
    case 'ui': {
      if (!entity) return spawn({ canvas: { document: path } });
      return { commands: [set(entity, 'canvas', { ...obj(entity.components?.canvas), document: path })] };
    }
    case 'physicsMaterial': {
      const c = entity?.components ?? {};
      if (entity && c.collider !== undefined) return { commands: [set(entity, 'collider', { ...obj(c.collider), material: path })] };
      if (entity && c.terrain !== undefined) return { commands: [set(entity, 'terrain', { ...obj(c.terrain), physicsMaterial: path })] };
      if (entity && c.tilemap !== undefined) return { commands: [set(entity, 'tilemap', { ...obj(c.tilemap), physicsMaterial: path })] };
      return { commands: [], message: 'Drop a physics material onto an object with a collider.' };
    }
  }
}

/** Material asset the target's mesh uses (to prefetch before a texture drop). */
export function materialAssetOf(doc: SceneDoc, id: string | null): string | null {
  if (!id) return null;
  const c = doc.entities.find((e) => e.id === id)?.components ?? {};
  const mesh = obj(c.mesh);
  return c.material === undefined && typeof mesh.material === 'string' && mesh.material ? mesh.material : null;
}
